import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { prescriptionService } from '../services/prescriptionService'
import { patientService } from '../services/patientService'
import { practiceLocationService } from '../services/practiceLocationService'
import { userService } from '../services/userService'
import { User } from '../types/user'
import { Prescription, PrescriptionStatus, PrescriptionType, ScriptType } from '../types/prescription'
import { printPrescription } from '../utils/printPrescription'
import { useAuth } from '../contexts/AuthContext'
import './PrescriptionList.css'

interface PrescriptionListProps {
  patientId: string
  onNewPrescription?: () => void
  onEditPrescription?: (prescription: Prescription) => void
  readOnly?: boolean
}

export function PrescriptionList({
  patientId,
  onNewPrescription,
  onEditPrescription,
  readOnly = false,
}: PrescriptionListProps) {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<PrescriptionStatus | 'all'>('all')
  const [typeFilter, setTypeFilter] = useState<PrescriptionType | 'all'>('all')
  const [scriptTypeFilter, setScriptTypeFilter] = useState<ScriptType | 'all'>('all')
  const canWrite = !readOnly

  const { data, isLoading, error } = useQuery({
    queryKey: ['prescriptions', patientId, statusFilter, typeFilter, scriptTypeFilter],
    queryFn: () =>
      prescriptionService.listPatientPrescriptions(patientId, {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        prescriptionType: typeFilter !== 'all' ? typeFilter : undefined,
        scriptType: scriptTypeFilter !== 'all' ? scriptTypeFilter : undefined,
      }),
  })

  const issueMutation = useMutation({
    mutationFn: (prescriptionId: string) => prescriptionService.issuePrescription(prescriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prescriptions', patientId], exact: false })
    },
  })

  const printMutation = useMutation({
    mutationFn: (prescriptionId: string) => prescriptionService.printPrescription(prescriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prescriptions', patientId], exact: false })
    },
  })

  const reorderMutation = useMutation({
    mutationFn: (prescriptionId: string) => prescriptionService.reorderPrescription(prescriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prescriptions', patientId], exact: false })
    },
  })

  const cancelMutation = useMutation({
    mutationFn: ({ prescriptionId, reason }: { prescriptionId: string; reason?: string }) =>
      prescriptionService.cancelPrescription(prescriptionId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prescriptions', patientId], exact: false })
    },
  })

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    } catch {
      return dateString
    }
  }

  const formatDateTime = (dateString?: string | null): string => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return dateString
    }
  }

  const prescriptions = useMemo(() => data?.items || [], [data])

  const handleIssue = async (prescription: Prescription) => {
    if (!canWrite) return
    if (
      !window.confirm(
        `Issue prescription dated ${formatDate(prescription.prescriptionDate)}?\n\nThis will mark the prescription as issued.`,
      )
    ) {
      return
    }

    try {
      await issueMutation.mutateAsync(prescription.prescriptionId)
    } catch (err: any) {
      console.error('Failed to issue prescription', err)
      window.alert(err?.response?.data?.message || 'Failed to issue prescription.')
    }
  }

  const { user: currentUser } = useAuth()

  const handlePrint = async (prescription: Prescription) => {
    if (prescription.status !== 'issued') {
      window.alert('Only issued prescriptions can be printed.')
      return
    }

    try {
      // Fetch patient, prescriber, and practice location data
      const [patient, practiceLocation] = await Promise.all([
        patientService.getById(patientId),
        practiceLocationService.getCurrent().catch(() => undefined), // Optional - don't fail if practice location not available
      ])

      // Fetch full user data to get signature if available
      let fullPrescriberData: User | null = null
      let signatureUrl: string | null = null
      
      if (currentUser?.id) {
        try {
          const allUsers = await userService.getAll()
          fullPrescriberData = allUsers.find(u => u.id === currentUser.id) || null
          
          // Fetch signature URL if signatureS3Key exists
          if (fullPrescriberData?.signatureS3Key) {
            try {
              signatureUrl = await userService.getSignatureUrl(currentUser.id)
            } catch (err) {
              console.warn('Failed to fetch signature URL:', err)
            }
          }
        } catch (err) {
          console.warn('Failed to fetch full user data for signature', err)
        }
      }

      // Create full User object with all required fields
      // Convert from auth User type (which may be missing some fields) to full User type
      const prescriber: User = currentUser
        ? {
            id: currentUser.id,
            username: currentUser.username || currentUser.email,
            email: currentUser.email,
            salutation: currentUser.salutation,
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            role: currentUser.role,
            roleCategory: currentUser.roleCategory,
            permissions: currentUser.permissions,
            isActive: true, // Default for active users
            createdAt: new Date().toISOString(), // Default if not available
            updatedAt: new Date().toISOString(), // Default if not available
            professionalBodyRegistrationNumber: (currentUser as any).professionalBodyRegistrationNumber || prescription.prescriberMedicalCouncilNumber,
            gmsNumber: (currentUser as any).gmsNumber,
            signatureS3Key: fullPrescriberData?.signatureS3Key || (currentUser as any).signatureS3Key,
            // Add signatureUrl directly to avoid fetching in print window
            signatureUrl: signatureUrl || undefined,
          } as User & { signatureUrl?: string }
        : {
            // Fallback: create a minimal user object from prescription data
            id: prescription.prescriberId,
            username: prescription.prescriberName.toLowerCase().replace(/\s+/g, '.'),
            email: `${prescription.prescriberName.toLowerCase().replace(/\s+/g, '.')}@clnicl.ie`,
            firstName: prescription.prescriberName.split(' ')[0] || '',
            lastName: prescription.prescriberName.split(' ').slice(1).join(' ') || '',
            role: 'doctor' as const,
            roleCategory: 'Clinical' as const,
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            professionalBodyRegistrationNumber: prescription.prescriberMedicalCouncilNumber,
          }

      // Print the prescription
      printPrescription(prescription, patient, prescriber, practiceLocation)

      // Mark as printed in backend
      await printMutation.mutateAsync(prescription.prescriptionId)
    } catch (err) {
      console.error('Failed to print prescription', err)
      window.alert('Failed to print prescription. Please try again.')
    }
  }

  const handleReorder = async (prescription: Prescription) => {
    if (!canWrite) return
    if (prescription.status !== 'issued') {
      window.alert('Only issued prescriptions can be reordered.')
      return
    }

    if (
      !window.confirm(
        `Create a new prescription based on this one?\n\nThis will create a new prescription with the same medications.`,
      )
    ) {
      return
    }

    try {
      const newPrescription = await reorderMutation.mutateAsync(prescription.prescriptionId)
      if (onEditPrescription) {
        onEditPrescription(newPrescription)
      }
    } catch (err) {
      console.error('Failed to reorder prescription', err)
      window.alert('Failed to reorder prescription.')
    }
  }

  const handleCancel = async (prescription: Prescription) => {
    if (!canWrite) return
    const reason = window.prompt('Cancel prescription?\n\nPlease enter a reason (optional):')
    if (reason === null) return

    try {
      await cancelMutation.mutateAsync({ prescriptionId: prescription.prescriptionId, reason: reason || undefined })
    } catch (err) {
      console.error('Failed to cancel prescription', err)
      window.alert('Failed to cancel prescription.')
    }
  }

  const getStatusBadgeClass = (status: PrescriptionStatus) => {
    switch (status) {
      case 'draft':
        return 'status-draft'
      case 'issued':
        return 'status-issued'
      case 'cancelled':
        return 'status-cancelled'
      default:
        return ''
    }
  }

  const checkRepeatAlerts = (prescription: Prescription) => {
    const alerts = []
    if (prescription.scriptType === 'repeat') {
      if (prescription.maxRepeats && prescription.currentRepeatCount >= prescription.maxRepeats) {
        alerts.push('Maximum repeats reached')
      }
      if (prescription.reviewDate) {
        const reviewDate = new Date(prescription.reviewDate)
        const today = new Date()
        if (reviewDate < today) {
          alerts.push('Review date has passed')
        }
      }
    }
    return alerts
  }

  if (isLoading) {
    return (
      <div className="prescription-list">
        <div className="loading">Loading prescriptions...</div>
      </div>
    )
  }

  if (error) {
    console.error('Error loading prescriptions:', error)
    return (
      <div className="prescription-list">
        <div className="error">
          <p>Error loading prescriptions: {error instanceof Error ? error.message : String(error)}</p>
          <p className="error-hint">
            If this is the first time accessing prescriptions, the backend may need to be deployed.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="prescription-list">
      <div className="prescription-list-header">
        <h3>Prescriptions</h3>
        {onNewPrescription && (
          <button
            type="button"
            className="btn-primary"
            onClick={onNewPrescription}
            disabled={!canWrite}
            title={canWrite ? undefined : 'Secondary records are read-only'}
          >
            New Prescription
          </button>
        )}
      </div>

      <div className="prescription-filters">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as PrescriptionStatus | 'all')}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="issued">Issued</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as PrescriptionType | 'all')}
          className="filter-select"
        >
          <option value="all">All Types</option>
          <option value="gms">GMS</option>
          <option value="private">Private</option>
        </select>

        <select
          value={scriptTypeFilter}
          onChange={(e) => setScriptTypeFilter(e.target.value as ScriptType | 'all')}
          className="filter-select"
        >
          <option value="all">All Scripts</option>
          <option value="acute">Acute</option>
          <option value="repeat">Repeat</option>
        </select>
      </div>

      {prescriptions.length === 0 ? (
        <div className="empty-state">
          <p>No prescriptions found.</p>
          {onNewPrescription && (
            <button
              type="button"
              className="btn-primary"
              onClick={onNewPrescription}
              disabled={!canWrite}
              title={canWrite ? undefined : 'Secondary records are read-only'}
            >
              Create First Prescription
            </button>
          )}
        </div>
      ) : (
        <div className="prescription-items">
          {prescriptions.map((prescription) => {
            const alerts = checkRepeatAlerts(prescription)
            return (
              <div key={prescription.prescriptionId} className="prescription-item">
                <div className="prescription-item-header">
                  <div className="prescription-item-title">
                    <span className={`status-badge ${getStatusBadgeClass(prescription.status)}`}>
                      {prescription.status}
                    </span>
                    <span className="prescription-type">
                      {prescription.prescriptionType.toUpperCase()} - {prescription.scriptType}
                    </span>
                    {prescription.isHandwritten && (
                      <span className="handwritten-badge" title="Handwritten prescription">
                        Handwritten
                      </span>
                    )}
                  </div>
                  <div className="prescription-item-date">{formatDate(prescription.prescriptionDate)}</div>
                </div>

                {alerts.length > 0 && (
                  <div className="prescription-alerts">
                    {alerts.map((alert, idx) => (
                      <div key={idx} className="alert-warning">
                        ⚠️ {alert}
                      </div>
                    ))}
                  </div>
                )}

                <div className="prescription-item-medications">
                  <strong>Medications ({prescription.medications.length}):</strong>
                  <ul>
                    {prescription.medications.slice(0, 3).map((med, idx) => (
                      <li key={idx}>
                        {med.name}
                        {med.dose && ` - ${med.dose}`}
                        {med.frequency && ` ${med.frequency}`}
                        {med.quantity && ` (${med.quantity.value} ${med.quantity.unit})`}
                      </li>
                    ))}
                    {prescription.medications.length > 3 && (
                      <li className="more-medications">+{prescription.medications.length - 3} more</li>
                    )}
                  </ul>
                </div>

                {prescription.scriptType === 'repeat' && (
                  <div className="prescription-repeat-info">
                    <span>
                      Repeat {prescription.currentRepeatCount}
                      {prescription.maxRepeats ? ` / ${prescription.maxRepeats}` : ''}
                    </span>
                    {prescription.reviewDate && (
                      <span>Review: {formatDate(prescription.reviewDate)}</span>
                    )}
                  </div>
                )}

                {prescription.instructions && (
                  <div className="prescription-instructions">
                    <strong>Instructions:</strong> {prescription.instructions}
                  </div>
                )}

                <div className="prescription-item-footer">
                  <div className="prescription-item-meta">
                    <span>Prescriber: {prescription.prescriberName}</span>
                    {prescription.printedAt && <span>Printed: {formatDateTime(prescription.printedAt)}</span>}
                  </div>
                  <div className="prescription-item-actions">
                    {prescription.status === 'draft' && (
                      <>
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => onEditPrescription?.(prescription)}
                          disabled={!canWrite}
                          title={canWrite ? undefined : 'Secondary records are read-only'}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => handleIssue(prescription)}
                          disabled={issueMutation.isPending || !canWrite}
                          title={canWrite ? undefined : 'Secondary records are read-only'}
                        >
                          Issue
                        </button>
                        <button
                          type="button"
                          className="btn-link text-danger"
                          onClick={() => handleCancel(prescription)}
                          disabled={cancelMutation.isPending || !canWrite}
                          title={canWrite ? undefined : 'Secondary records are read-only'}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {prescription.status === 'issued' && (
                      <>
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => handlePrint(prescription)}
                          disabled={printMutation.isPending}
                        >
                          Print
                        </button>
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => handleReorder(prescription)}
                          disabled={reorderMutation.isPending || !canWrite}
                          title={canWrite ? undefined : 'Secondary records are read-only'}
                        >
                          Reorder
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

